import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import 'styles/panel/settings.panel.css'
import {
  XIcon,
  PencilSimpleIcon,
  TrashIcon,
  SpinnerGapIcon
} from '@phosphor-icons/react'
import { useAuth } from 'context/auth.context'
import { useProfile } from 'context/profile.context'
import BottomDrawer from 'components/drawers/bottom.drawer'
import UpdatePanel from 'components/panels/update/update.panel'
import { formatDate } from 'utilities/global.functions'


const SettingsPanel = ({ onClose }) => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const {
    displayName,
    birthYear,
    currency,
    defaultView,
    targetNetworth,
    targetAge,
    profilePicSignedUrl,
    uploadProfilePic,
    removeProfilePic,
    updateProfile
  } = useProfile()

  const [localDisplayName, setLocalDisplayName] = useState(displayName ?? '')
  const [localYearOfBirth, setLocalYearOfBirth] = useState(birthYear ? String(birthYear) : '')
  const [localCurrency, setLocalCurrency] = useState(currency ?? 'GBP')
  const [localView, setLocalView] = useState(defaultView ?? 'tiles')
  const [localTargetNetworth, setLocalTargetNetworth] = useState(String(targetNetworth))
  const [localTargetAge, setLocalTargetAge] = useState(String(targetAge))
  const [changed, setChanged] = useState(false)
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    setSaving(true)
    try {
      await updateProfile({
        display_name: localDisplayName,
        birth_year: localYearOfBirth ? Number(localYearOfBirth) : null,
        currency: localCurrency,
        default_view: localView,
        // Both target columns are NOT NULL, so an emptied field keeps the current value
        target_networth: Number(localTargetNetworth) || targetNetworth,
        target_age: Number(localTargetAge) || targetAge,
      })
      setChanged(false)
    } finally {
      setSaving(false)
    }
  }

  const [isUpdating, setIsUpdating] = useState(false)
  const [syncing, setSyncing] = useState(false)

  const fileInputRef = useRef(null)

  const handleProfilePicClick = () => fileInputRef.current?.click()

  const handleFileChange = async e => {
    const file = e.target.files?.[0]
    if (!file) return
    setSyncing(true)
    try {
      await uploadProfilePic(file)
    } finally {
      setSyncing(false)
      e.target.value = ''
    }
  }

  const handleRemoveProfilePic = async e => {
    e.stopPropagation()
    setSyncing(true)
    try {
      await removeProfilePic()
    } finally {
      setSyncing(false)
    }
  }



  return (
    <>
      <div className='SettingsPanel light'>

          <div className='flex row space center'>
            <p className='xlarge '>Settings</p>

            <div className='icon large pointer'>
              {' '}
              {onClose && <XIcon  onClick={onClose} />}{' '}
            </div>
          </div>

          <p className='medium o80'>
            Manage your account.
          </p>
   <div className='flex col large-gap margin-top-large '>
          <div className='r4-1 flex row large-gap padding '>
            <div
              className='ProfilePic '
              onClick={handleProfilePicClick}
            >
              {profilePicSignedUrl ? (
                <img src={profilePicSignedUrl} alt='profile pic' />
              ) : (
                <p className='xxlarge'>
                  {(displayName || user?.email || '?')[0].toUpperCase()}
                </p>
              )}
              <div className='ProfilePicOverlay'>
                {syncing ? (
                  <SpinnerGapIcon size={14} className='spin' />
                ) : profilePicSignedUrl ? (
                  <TrashIcon size={14} onClick={handleRemoveProfilePic} />
                ) : (
                  <PencilSimpleIcon size={14} onClick={handleProfilePicClick} />
                )}
              </div>
              <input
                ref={fileInputRef}
                type='file'
                accept='image/*'
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
            </div>

            <div className='flex col space margin-top-small'>
              <div>
            <p className='large'>{displayName}</p>
                <p className='large'>{user?.email}</p>
              </div>

<div className='flex row medium-gap'>


            <p className='flag '> Member since: {formatDate(user?.created_at)}</p>
              <p className='flag  '> Last signed in: {formatDate(user?.last_sign_in_at)}</p>
            </div>
    <div>
                <button className='button medium' onClick={() => navigate('/logout')}>Logout</button>

              </div>
            </div>
          </div>




                  {/*  PROFILE */ }
          <div className='card  margin-top-large'>
            <p className='medium'>Profile</p>
            <div className='flex col medium-gap '>
              <div className='form-group margin-top-large'>
                <div className='form-item'>
                  <label>Display name</label>
                  <input type='text' className='medium' value={localDisplayName} onChange={e => { setLocalDisplayName(e.target.value); setChanged(true) }} />
                </div>
                <div className='form-item'>
                  <label>Year of birth</label>
                  <input type='text' inputMode='numeric' pattern='[0-9]{4}' maxLength={4} placeholder='YYYY' className='medium' value={localYearOfBirth} onChange={e => { setLocalYearOfBirth(e.target.value); setChanged(true) }} />
                </div>
              </div>
            </div>
          </div>

                  {/*  TARGETS */ }
          <div className='card '>
            <p className='medium'>Targets</p>
            <div className='flex col medium-gap '>
              <div className='form-group margin-top-large'>
                <div className='form-item'>
                  <label>Target net worth</label>
                  <input type='text' inputMode='numeric' pattern='[0-9]*' placeholder='500000' className='medium' value={localTargetNetworth} onChange={e => { setLocalTargetNetworth(e.target.value.replace(/[^0-9]/g, '')); setChanged(true) }} />
                </div>
                <div className='form-item'>
                  <label>Target retirement age</label>
                  <input type='text' inputMode='numeric' pattern='[0-9]{1,3}' maxLength={3} placeholder='65' className='medium' value={localTargetAge} onChange={e => { setLocalTargetAge(e.target.value.replace(/[^0-9]/g, '')); setChanged(true) }} />
                </div>
              </div>
            </div>
          </div>

          <div className='card '>
            <p className='medium'>Preferences</p>
            <div className='flex col medium-gap '>
              <div className='form-group margin-top-large'>
                <div className='form-item'>
                  <label>Default view</label>
                  <select className='medium' value={localView} onChange={e => { setLocalView(e.target.value); setChanged(true) }}>
                    <option value='tiles'>Tiles</option>
                    <option value='tables'>Tables</option>
                  </select>
                </div>
                <div className='form-item'>
                  <label>Currency</label>
                  <select className='medium' value={localCurrency} onChange={e => { setLocalCurrency(e.target.value); setChanged(true) }}>
                    <option value='GBP'>GBP — British Pound</option>
                    <option value='USD'>USD — US Dollar</option>
                    <option value='EUR'>EUR — Euro</option>
                    <option value='CAD'>CAD — Canadian Dollar</option>
                    <option value='AUD'>AUD — Australian Dollar</option>
                    <option value='CHF'>CHF — Swiss Franc</option>
                    <option value='JPY'>JPY — Japanese Yen</option>
                    <option value='SGD'>SGD — Singapore Dollar</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* <div className='card'>
            <p className='medium'>Debugging</p>
            <div className=' '>
              <div className='form-group flex col large-gap margin-top-large'>
                <div className='form-item'>
                  <label>User ID</label>
                  <p className='medium'>{user?.id}</p>
                </div>
                <div className='form-item'>
                  <label>Email</label>
                  <p className='medium'>{user?.email}</p>
                </div>
                <div className='form-item'>
                  <label>Email Confirmed</label>
                  <p className='medium'>
                    {user?.email_confirmed_at
                      ? formatDate(user.email_confirmed_at)
                      : 'Not confirmed'}
                  </p>
                </div>
                <div className='form-item'>
                  <label>Provider</label>
                  <p className='medium'>{user?.app_metadata?.provider}</p>
                </div>
                <div className='form-item'>
                  <label>Account Created</label>
                  <p className='medium'>{formatDate(user?.created_at)}</p>
                </div>
                <div className='form-item'>
                  <label>Last Sign In</label>
                  <p className='medium'>{formatDate(user?.last_sign_in_at)}</p>
                </div>
                <div className='form-item'>
                  <label>Token Type</label>
                  <p className='medium'>{session?.token_type}</p>
                </div>
                <div className='form-item'>
                  <label>Expires</label>
                  <p className='medium'>
                    {formatDateTime(session?.expires_at)}
                  </p>
                </div>
                <div className='form-item'>
                  <label>Access Token</label>
                  <p className='medium'>
                    {truncateToken(session?.access_token)}
                  </p>
                </div>
              </div>
            </div>
          </div> */}


        </div>

        <div className='flex col regular-gap margin-top-large'>
          <button className='Save button large' disabled={!changed || saving} onClick={handleSave}>
            {saving ? 'Saving...' : 'Save preferences'}
          </button>
          <button className='Cancel button large' onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>

      {isUpdating && (
        <BottomDrawer onCancel={() => setIsUpdating(false)}>
          <UpdatePanel onCancel={() => setIsUpdating(false)} />
        </BottomDrawer>
      )}
    </>
  )
}

export default SettingsPanel
